import { useEffect, useRef, useState } from 'react';
import { geocode } from '../../lib/leaflet';

// Center used when a hotel has no coordinates yet (roughly the centre of India).
const DEFAULT_CENTER = [20.5937, 78.9629];

// One hotel = one card: a Leaflet/OpenStreetMap map pinned at its lat/lon, a
// keyless Nominatim search that recentres the map/pin (preview only — nothing is
// saved), and its details.
export default function HotelMapCard({ hotel, mapsReady }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const hasCoords = hotel.lat != null && hotel.lon != null;

  // Initialise the map once Leaflet is ready (and re-init if a different hotel shows).
  useEffect(() => {
    const L = window.L;
    if (!mapsReady || !L || !mapEl.current) return;

    const center = hasCoords ? [Number(hotel.lat), Number(hotel.lon)] : DEFAULT_CENTER;
    const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView(center, hasCoords ? 15 : 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    if (hasCoords) markerRef.current = L.marker(center).addTo(map);
    // Container is sized by CSS after mount — nudge Leaflet to recompute.
    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsReady, hotel.id]);

  // Debounced keyless geocoding — no auto-search, stays within Nominatim's policy.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) { setResults([]); return; }
    const controller = new AbortController();
    setSearching(true);
    const t = setTimeout(() => {
      geocode(q, { signal: controller.signal })
        .then(setResults)
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 450);
    return () => { controller.abort(); clearTimeout(t); };
  }, [query]);

  const pick = (r) => {
    const L = window.L;
    const map = mapRef.current;
    const pos = [Number(r.lat), Number(r.lon)];
    if (L && map) {
      map.setView(pos, 16);
      if (markerRef.current) markerRef.current.setLatLng(pos);
      else markerRef.current = L.marker(pos).addTo(map);
    }
    setResults([]);
    setQuery(r.display_name);
  };

  return (
    <div className="map-card">
      <div className="map-card__head">
        <h3 className="map-card__name">{hotel.name || 'Unnamed hotel'}</h3>
        {hotel.city && <span className="map-card__city">{hotel.city}</span>}
      </div>

      <div className="map-card__search-wrap">
        <input
          className="map-card__search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={mapsReady ? 'Search this hotel on the map…' : 'Map loading…'}
          disabled={!mapsReady}
        />
        {searching && <span className="map-card__search-hint">Searching…</span>}
        {results.length > 0 && (
          <ul className="geo-results">
            {results.map((r) => (
              <li key={r.place_id}>
                <button type="button" className="geo-results__item" onClick={() => pick(r)}>
                  {r.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="map-card__map-wrap">
        <div ref={mapEl} className="map-card__map" />
        {!mapsReady && <div className="map-card__map-note">Loading map…</div>}
      </div>

      <div className="map-card__meta">
        <div className="map-card__addr">
          {hotel.address || <span className="badge badge--warn">address missing</span>}
        </div>
        <div className="map-card__coords">
          {hasCoords ? (
            <span className="mono">{hotel.lat}, {hotel.lon}</span>
          ) : (
            <span className="badge badge--warn">no coordinates</span>
          )}
        </div>
      </div>
    </div>
  );
}
